const CDN_PROVIDERS = {
  skypack: {
    url: 'https://cdn.skypack.dev',
    formatUrl: (pkg, version) =>
      `${CDN_PROVIDERS.skypack.url}/${pkg}${version !== 'latest' ? `@${version}` : ''}`
  },
  symbols: {
    url: 'https://pkg.symbo.ls',
    formatUrl: (pkg, version) => {
      const onlyDotsAndNumbers = (str) => /^[0-9.]+$/.test(str) && str !== ''
      if (pkg.split('/').length > 2 || !onlyDotsAndNumbers(version)) {
        return `${CDN_PROVIDERS.symbols.url}/${pkg}`
      }
      return `${CDN_PROVIDERS.symbols.url}/${pkg}/${version}.js`
    }
  }
}

export const getCDNUrl = (
  packageName,
  version = 'latest',
  provider = 'skypack'
) => {
  const cdnConfig = CDN_PROVIDERS[provider] || CDN_PROVIDERS.skypack
  return cdnConfig.formatUrl(packageName, version)
}

/**
 * Asynchronously loads a package module, with a fallback for Skypack.
 * Assumes 'getCDNUrl' and 'options.window' are available in scope.
 *
 * @param {string} key - The package name/key.
 * @param {string} url - The primary import URL.
 * @param {object} options - Options object containing 'window'.
 * @param {string} provider - The CDN provider (e.g., 'skypack').
 * @param {string} random - A random string for cache busting.
 * @returns {Promise<any>} The loaded package module.
 */
export async function loadModule(key, url, options, provider, random) {
  // Helper function to execute dynamic import via Function constructor
  const createDynamicImport = (win, importUrl) => {
    const importFn = win.Function('url', 'return import(url)')
    return importFn(importUrl)
  }

  const win = options.window

  try {
    // --- Primary Import Attempt ---
    const mod = await createDynamicImport(win, url)
    const pkg = mod.default || mod
    win.packages[key] = pkg
    return pkg
  } catch (primaryError) {
    // --- Skypack Fallback Attempt ---
    if (provider === 'skypack') {
      const fallbackUrl = getCDNUrl(key, 'latest', 'symbols') + random

      try {
        const fallbackMod = await createDynamicImport(win, fallbackUrl)
        const fallbackPkg = fallbackMod.default || fallbackMod
        win.packages[key] = fallbackPkg
        return fallbackPkg
      } catch (fallbackError) {
        // Fallback failed, proceed to final error handling and throw the original primaryError
        // The original logic dictates we throw the first error if the fallback fails.
      }
    }

    // --- Final Error Handling (Logs and Re-throws) ---
    // This catches:
    // 1. The primaryError if provider is not 'skypack'.
    // 2. The primaryError if the 'skypack' fallback also failed.
    console.error(`Failed to load ${key}:`, primaryError)
    throw primaryError
  }
}
